import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import NemeanLion from '../labors/NemeanLion'
import { ChevronRight, Swords } from 'lucide-react';
import { Speech } from 'lucide-react';



const labors = [
    { id: 1, name: "The Nemean Lion", component: NemeanLion },
];

export const Vocab: React.FC = () => {
    const [selectedLabor, setSelectedLabor] = useState<number | null>(null);

    return (
        <div className="flex h-screen min-h-[600px] border border-stone-dark overflow-hidden bg-warm-cream font-cinzel">
            <div className="w-1/3 bg-stone-light p-4 overflow-y-auto">
                <h2 className="text-2xl font-semibold mb-6 flex items-center text-terracotta border-b-2 border-terracotta pb-2">
                    <Swords className="mr-2" />Labors of Hercules
                </h2>
                <div className="flex flex-col gap-3">
                    {labors.map((labor) => (
                        <button
                            key={labor.id}
                            onClick={() => setSelectedLabor(labor.id)}
                            className={`flex items-center justify-between p-3 rounded-lg transition-colors duration-200 ${selectedLabor === labor.id
                                ? 'bg-golden-yellow text-greek-black'
                                : 'hover:bg-stone text-greek-black hover:text-warm-cream'
                                }`}
                        >
                            <span className="flex items-center text-lg">
                                Labor {labor.id} - {labor.name}
                            </span>
                            <ChevronRight size={20} className={selectedLabor === labor.id ? 'text-greek-black' : 'text-stone-dark'} />
                        </button>
                    ))}
                </div>
            </div>
            <div className="w-px bg-stone-dark" />
            {/* displays the labor component created in labors folder*/}
            <div className="flex-1 p-6 overflow-y-auto bg-warm-cream">
                {selectedLabor ? (React.createElement(labors.find(labor => labor.id === selectedLabor)?.component || (() => null))
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <svg className="w-16 h-16 mb-4 text-aegean-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xl text-greek-black">Select a labor to view its details.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

type LetterProps = {
    greek: string;
    name: string;
    highlight?: 'green' | 'purple';
    audio: string;
};

const Letter: React.FC<LetterProps> = ({ greek, name, highlight, audio }) => {
    const handlePronounce = (audioPath: string) => {
        const audio = new Audio(audioPath);
        audio.play().catch(error => console.error("Error playing audio:", error));
    };

    const [showTooltip, setShowTooltip] = useState(false);

    const highlightClass = highlight === 'green'
        ? 'bg-olive-green text-warm-cream'
        : highlight === 'purple'
            ? 'bg-purple-400 text-greek-black'
            : 'bg-warm-cream text-greek-black';

    return (
        <div className={`p-3 m-1 rounded-lg shadow-md ${highlightClass} flex items-center justify-between`}>
            <div>
                <span className="font-bold font-cinzel text-lg">{greek}</span> - <span className="text-sm">{name}</span>
            </div>
            <div className="relative group">
                <button
                    className='ml-2 text-greek-black'
                    onClick={() => handlePronounce(audio || '')}
                    aria-label={`Pronounce ${name}`}
                >
                    <Speech size={24} />
                </button>
                <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 right-0 bottom-full mb-2">
                    Pronounce {name}
                </span>
            </div>
        </div>
    );
};


export const Alphabet: React.FC = () => {

    const letters: LetterProps[] = [
        { greek: 'Αα', name: 'aa', audio: "/audio/alphabet/1a.m4a" },
        { greek: 'Ββ', name: 've', audio: "/audio/alphabet/2a.m4a" },
        { greek: 'Γγ', name: 'ge', audio: "/audio/alphabet/3a.m4a" },
        { greek: 'Δδ', name: 'the', audio: "/audio/alphabet/4a.m4a" },
        { greek: 'Εε', name: 'e', highlight: 'green', audio: "/audio/alphabet/5a.m4a" },
        { greek: 'Ζζ', name: 'zee', audio: "/audio/alphabet/6a.m4a" },
        { greek: 'Ηη', name: 'e', highlight: 'green', audio: "/audio/alphabet/7a.m4a" },
        { greek: 'Θθ', name: 'th', highlight: 'green', audio: "/audio/alphabet/8a.m4a" },
        { greek: 'Ιι', name: 'e', highlight: 'green', audio: "/audio/alphabet/9a.m4a" },
        { greek: 'Κκ', name: 'k', audio: "/audio/alphabet/10a.m4a" },
        { greek: 'Λλ', name: 'L', audio: "/audio/alphabet/11a.m4a" },
        { greek: 'Μμ', name: 'me', audio: "/audio/alphabet/12a.m4a" },
        { greek: 'Νν', name: 'nee', audio: "/audio/alphabet/13a.m4a" },
        { greek: 'Ξξ', name: 'xse', audio: "/audio/alphabet/14a.m4a" },
        { greek: 'Οο', name: 'oo', highlight: 'purple', audio: "/audio/alphabet/15a.m4a" },
        { greek: 'Ππ', name: 'peee', audio: "/audio/alphabet/16a.m4a" },
        { greek: 'Ρρ', name: 'ro', audio: "/audio/alphabet/17a.m4a" },
        { greek: 'Σσ,ς', name: 'sss', audio: "/audio/alphabet/18a.m4a" },
        { greek: 'Ττ', name: 'taf', audio: "/audio/alphabet/19a.m4a" },
        { greek: 'Υυ', name: 'eepsilon', audio: "/audio/alphabet/20a.m4a" },
        { greek: 'Φφ', name: 'fee', audio: "/audio/alphabet/21a.m4a" },
        { greek: 'Χχ', name: 'hee', audio: "/audio/alphabet/22a.m4a" },
        { greek: 'Ψψ', name: 'psee', audio: "/audio/alphabet/23a.m4a" },
        { greek: 'Ωω', name: 'oo', highlight: 'purple', audio: "/audio/alphabet/24a.m4a" },
    ];

    return (
        <div className="p-6 bg-stone min-h-screen text-greek-black shadow-lg">
            <h2 className="text-3xl font-bold mb-4 font-cinzel text-greek-black">Greek Alphabet</h2>
            <p className="mb-4 text-greek-black">
                Greek is a phonetic alphabet. Use the sounds to start pronouncing the words you see.
            </p>
            <p className="mb-2">
                <span className="bg-olive-green text-warm-cream px-2 py-1 rounded">Green highlighted letters</span> make the same sound "e"
            </p>
            <p className="mb-4">
                <span className="bg-purple-400 text-warm-cream px-2 py-1 rounded">Purple highlighted letters</span> make the same sound "o"
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {letters.map((letter, index) => (
                    <Letter key={index} {...letter} />
                ))}
            </div>
        </div>
    );
}


const ResourcesPage: React.FC = () => {
    return (
        <div style={{
            backgroundImage: "url('/images/greecesky.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
        }}>
            <div className="min-h-screen w-screen" style={{
                backgroundImage: "url('/images/temple.png')",
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}>
                <div className="container mx-auto pt-4 pb-8">
                    <div className="flex align-left mb-4">
                        <div className="relative" style={{
                            backgroundImage: "url('/images/scroll_button.png')",
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            width: '250px',
                            height: '125px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <span className="text-3xl font-bold text-greek-black font-cinzel" style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textShadow: '1px 1px 2px rgba(255,255,255,0.7)'
                            }}>
                                Resources
                            </span>
                        </div>
                    </div>
                    <ul className="space-y-2">
                        <li>
                            <Link to="/dictionary">
                                <div className="relative" style={{
                                    backgroundImage: "url('/images/scroll_button.png')",
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                    width: '150px',
                                    height: '75px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <span className="text-1xl font-bold text-greek-black font-cinzel">
                                        Dictionary
                                    </span>
                                </div>
                            </Link>
                        </li>
                        <li>
                            <Link to="/vocabulary">
                                <div className="relative" style={{
                                    backgroundImage: "url('/images/scroll_button.png')",
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                    width: '150px',
                                    height: '75px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <span className="text-1xl font-bold text-greek-black font-cinzel">
                                        Vocabulary
                                    </span>
                                </div>
                            </Link>
                        </li>
                        <li>
                            <Link to="/alphabet">
                                <div className="relative" style={{
                                    backgroundImage: "url('/images/scroll_button.png')",
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                    width: '150px',
                                    height: '75px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <span className="text-1xl font-bold text-greek-black font-cinzel">
                                        Alphabet
                                    </span>
                                </div>
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ResourcesPage;